// Custom node renderer for Sigma.js with lock icon overlay support

/**
 * Create a lock icon as a data URL for WebGL rendering
 * @param {number} size - Size of the icon
 * @param {string} color - Color of the icon
 * @returns {string} Data URL of the SVG icon
 */
const createLockIconDataURL = (size = 24, color = '#ffffff') => {
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 10V8C6 5.79086 7.79086 4 10 4H14C16.2091 4 18 5.79086 18 8V10"
        stroke="${color}"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <rect
        x="4"
        y="10"
        width="16"
        height="10"
        rx="2"
        ry="2"
        fill="${color}"
        stroke="${color}"
        stroke-width="2"
      />
      <circle
        cx="12"
        cy="15"
        r="1.5"
        fill="#000000"
      />
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Load an image from a data URL
 * @param {string} url - Data URL of the image
 * @returns {Promise<HTMLImageElement>} Promise that resolves to the loaded image
 */
const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
};

// Cache for lock icon images
const lockIconCache = new Map();

/**
 * Get or create a lock icon image
 * @param {number} size - Size of the icon
 * @param {string} color - Color of the icon
 * @returns {Promise<HTMLImageElement>} Promise that resolves to the lock icon image
 */
export const getLockIcon = async (size = 24, color = '#ffffff') => {
  const key = `${size}-${color}`;
  
  if (lockIconCache.has(key)) {
    return lockIconCache.get(key);
  }
  
  const dataURL = createLockIconDataURL(size, color);
  const image = await loadImage(dataURL);
  lockIconCache.set(key, image);
  
  return image;
};

/**
 * Custom node renderer that handles lock icon overlays
 * This is a higher-order function that creates a node renderer
 * @param {Object} options - Configuration options
 * @returns {Object} Node renderer configuration
 */
export const createNodeWithLockRenderer = (options = {}) => {
  return {
    // Use the standard circle renderer as base
    type: 'circle',
    
    // Custom rendering logic that will be applied after the base circle
    customRenderer: async (context, node, settings) => {
      // Only render lock icon for private nodes
      if (node.visibility !== 'private') {
        return;
      }
      
      try {
        // Get the lock icon image
        const lockIcon = await getLockIcon(12, '#ffffff'); // Small white lock icon
        
        // Calculate the position to center the icon on the node
        const nodeSize = node.size || 10;
        const iconSize = Math.min(nodeSize * 0.6, 12); // Icon size is 60% of node size, max 12px
        const x = node.x - iconSize / 2;
        const y = node.y - iconSize / 2;
        
        // Draw the lock icon
        context.drawImage(lockIcon, x, y, iconSize, iconSize);
      } catch (error) {
        console.warn('Failed to render lock icon:', error);
      }
    }
  };
};

/**
 * Alternative approach using DOM overlay for lock icons
 * This creates HTML elements positioned over the canvas
 */
export class LockIconOverlay {
  constructor(sigmaInstance, containerElement) {
    this.sigma = sigmaInstance;
    this.container = containerElement;
    this.overlayContainer = null;
    this.lockElements = new Map();
    
    this.createOverlayContainer();
    this.setupEventListeners();
  }
  
  createOverlayContainer() {
    this.overlayContainer = document.createElement('div');
    this.overlayContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 10;
    `;
    this.container.appendChild(this.overlayContainer);
  }
  
  setupEventListeners() {
    // Update lock positions when camera moves
    this.sigma.getCamera().on('updated', () => {
      this.updateLockPositions();
    });
    
    // Update when graph is rendered
    this.sigma.on('afterRender', () => {
      this.updateLockIcons();
    });
  }
  
  updateLockIcons() {
    const graph = this.sigma.getGraph();
    const camera = this.sigma.getCamera();
    
    // Clear existing lock elements
    this.clearLockElements();
    
    // Add lock icons for private nodes
    graph.forEachNode((nodeId, nodeData) => {
      if (nodeData.visibility === 'private') {
        this.createLockElement(nodeId, nodeData, camera);
      }
    });
  }
  
  createLockElement(nodeId, nodeData, camera) {
    const coords = this.sigma.graphToViewport(nodeData);
    const nodeSize = nodeData.size || 10;
    const iconSize = Math.min(nodeSize * 0.6 * camera.ratio, 16); // Scale with zoom
    
    // Only show if the icon would be visible (not too small)
    if (iconSize < 4) return;
    
    const lockElement = document.createElement('div');
    lockElement.style.cssText = `
      position: absolute;
      left: ${coords.x - iconSize / 2}px;
      top: ${coords.y - iconSize / 2}px;
      width: ${iconSize}px;
      height: ${iconSize}px;
      pointer-events: none;
      z-index: 15;
    `;
    
    // Create SVG lock icon
    lockElement.innerHTML = `
      <svg width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none">
        <path
          d="M6 10V8C6 5.79086 7.79086 4 10 4H14C16.2091 4 18 5.79086 18 8V10"
          stroke="#ffffff"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <rect
          x="4"
          y="10"
          width="16"
          height="10"
          rx="2"
          ry="2"
          fill="#ffffff"
          stroke="#ffffff"
          stroke-width="2"
        />
        <circle
          cx="12"
          cy="15"
          r="1.5"
          fill="#000000"
        />
      </svg>
    `;
    
    this.overlayContainer.appendChild(lockElement);
    this.lockElements.set(nodeId, lockElement);
  }
  
  updateLockPositions() {
    const graph = this.sigma.getGraph();
    const camera = this.sigma.getCamera();
    
    this.lockElements.forEach((element, nodeId) => {
      if (graph.hasNode(nodeId)) {
        const nodeData = graph.getNodeAttributes(nodeId);
        const coords = this.sigma.graphToViewport(nodeData);
        const nodeSize = nodeData.size || 10;
        const iconSize = Math.min(nodeSize * 0.6 * camera.ratio, 16);
        
        // Update position and size
        element.style.left = `${coords.x - iconSize / 2}px`;
        element.style.top = `${coords.y - iconSize / 2}px`;
        element.style.width = `${iconSize}px`;
        element.style.height = `${iconSize}px`;
        
        // Hide if too small
        element.style.display = iconSize < 4 ? 'none' : 'block';
        
        // Update SVG size
        const svg = element.querySelector('svg');
        if (svg) {
          svg.setAttribute('width', iconSize);
          svg.setAttribute('height', iconSize);
        }
      }
    });
  }
  
  clearLockElements() {
    this.lockElements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
    this.lockElements.clear();
  }
  
  destroy() {
    this.clearLockElements();
    if (this.overlayContainer && this.overlayContainer.parentNode) {
      this.overlayContainer.parentNode.removeChild(this.overlayContainer);
    }
  }
}
